import React, { useEffect, useState } from 'react';
import SearchBar from '../../../components/SearchBar/SearchBar';
import MLButton from '../../../components/MLButton';
import ExportDialog from '../../../components/ExportDialog/ExportDialog';
import { useExportStudentActivity } from '../../hooks/useExportStudentActivity';
import styles from './StudentActivityContent.module.css';
import MLIcon from '../../../components/MLIcon';
import { StudentActivityTable } from './StudentActivityTable';
import { filterStudentsBySearch } from '../../utils/studentFilters';

export const StudentActivityContent = ({
  students,
  days = 7,
  studentsWithoutActivity,
  handleISGLaunch,
  setOpen,
  apiHost,
  courseId,
}) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const filteredStudents = filterStudentsBySearch(students, searchValue);

  const resetModalState = () => {
    setIsLoading(false);
    setShowExportDialog(false);
    setShouldFetch(false);
  };

  const exportQuery = useExportStudentActivity(apiHost, courseId, shouldFetch);

  // Close Export modal when export is complete
  useEffect(() => {
    if (!exportQuery.isLoading && shouldFetch) {
      resetModalState();
    }
  }, [exportQuery.isLoading, shouldFetch]);

  const handleConfirmedExport = () => {
    setIsLoading(true);
    setShouldFetch(true);
  };

  const handleCancelExport = () => {
    resetModalState();
  };

  const handleExportClick = () => {
    setShowExportDialog(true);
  };

  const renderHeaderWarning = () => {
    const dayCounter = days === 1 ? 'day' : `${days} days`;
    const studentCounter =
      studentsWithoutActivity.length === 1 ? 'student has' : 'students have';

    return (
      <div className={styles.headerWarning}>
        <MLIcon type="warning" className={styles.warningIcon} />
        <div
          className={styles.warningText}
          data-test-id="inactive_alert_warning_text"
        >
          {studentsWithoutActivity.length} {studentCounter} not opened an
          activity in the last {dayCounter}.
        </div>
      </div>
    );
  };

  const renderHeaderDescription = () => {
    return (
      <div className={styles.headerDescription}>
        The list below includes all students enrolled in your course and their
        most recent activity timestamp.{' '}
        <strong>Inactive students are marked with the alert icon.</strong>
      </div>
    );
  };

  const renderSearchAndExportButton = () => {
    return (
      <div className={styles.searchAndExportContainer}>
        <SearchBar
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          testId="inactive_alert_search_students_bar"
        />
        <MLButton
          btnType="secondary"
          btnText="Export List"
          onClick={handleExportClick}
          testId="inactive_alert_export_list_btn"
        />
      </div>
    );
  };

  return (
    <div className={styles.contentContainer}>
      <ExportDialog
        show={showExportDialog}
        onClose={handleCancelExport}
        onExport={handleConfirmedExport}
        onCancel={handleCancelExport}
        isLoading={isLoading}
        title="Export Student Activity List"
        description="This export includes the name and email of every student in the course along with their most recent activity timestamp. The list is exported as a CSV file."
        loadingMessage="We're exporting the list of student activity launches. This could take a few minutes to complete."
        confirmExportTestId="inactive_alert_confirm_export_btn"
        cancelExportTestId="inactive_alert_cancel_export_btn"
        cancelDuringExportTestId="inactive_alert_cancel_during_export_btn"
      />
      {renderHeaderWarning()}
      {renderHeaderDescription()}
      {renderSearchAndExportButton()}
      <StudentActivityTable
        students={filteredStudents}
        studentsWithoutActivity={studentsWithoutActivity}
        handleISGLaunch={handleISGLaunch}
        setOpen={setOpen}
        days={days}
      />
    </div>
  );
};
